/**
 * RequestKillModal — an officer asks SysOps to kill one record.
 *
 * Bill 2026-09-15: "we do need to allow FSRs to request KILLs on any items as
 * they do now." The request becomes a Kill List entry waiting for SysOps to
 * verify; SysOps gets a Teams card and executes it from the SysOps panel, where
 * the kill order (NetTeller, debit card, accounts, CIF) is enforced.
 */
import { useState } from 'react';
import type { KillEntryType } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';

export interface KillTarget {
  /** Core Director number of the record itself (CIF, account, ATM or NetTeller record). */
  id: string;
  /** 'CIF' or the Core Director Maj Type code (10/20/30/50/60/91/92). */
  type: string;
  /** Plain-language description shown to the officer, e.g. "Checking 1234". */
  label: string;
}

export interface RequestKillModalProps {
  target: KillTarget | null;
  cif: string;
  customerName: string;
  onClose: () => void;
  onRequested: (targetId: string) => void;
}

function entryTypeFor(type: string): KillEntryType {
  if (type === 'CIF') return 'CifKill';
  if (type === '91') return 'CardRevoke';
  if (type === '92') return 'NetTellerRevoke';
  return 'AccountKill';
}

export function RequestKillModal({ target, cif, customerName, onClose, onRequested }: RequestKillModalProps): JSX.Element | null {
  const client = useApiClient();
  const { user } = useAuth();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!target) return null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await client.retail.killList.create({
        entryType: entryTypeFor(target.type),
        targetId: target.id,
        targetType: target.type,
        reason: 'FsrRequest',
        customerName,
        createdBy: user?.upn,
        reasonDetailsJson: JSON.stringify({ note: note.trim() }),
        metadataJson: JSON.stringify({ source: 'officer-request', cif }),
      });
      setNote('');
      onRequested(target.id);
    } catch (e: unknown) {
      setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      open
      size="md"
      title="Request a kill"
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || note.trim().length < 5}>
            {busy ? 'Sending…' : 'Send to SysOps'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{target.label}</div>
          <div style={{ color: 'var(--ink-600)' }}>
            {customerName} · CIF {cif}
          </div>
        </div>
        <p style={{ margin: 0, color: 'var(--ink-700)' }}>
          SysOps verifies the request and kills the record in Core Director. NetTeller, debit cards and accounts on
          this customer are handled first when they have to be.
        </p>
        <label htmlFor="rop-kill-note" style={{ display: 'block', fontSize: 12, color: 'var(--ink-700)' }}>
          Why should this record be killed?
        </label>
        <textarea
          id="rop-kill-note"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          maxLength={500}
          placeholder="For example: opened in error, duplicate customer, test record"
          style={{
            width: '100%',
            minHeight: 80,
            padding: '8px 10px',
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
        {err && <Banner tone="err">{err}</Banner>}
      </div>
    </ModalShell>
  );
}
